// ════════════════════════════════════════════════════════════════
// ReportService.js
// PDF report generation for Adyantam — Flat Owner YTD Report
// and Tenant Report. Uses jsPDF + jspdf-autotable.
// ════════════════════════════════════════════════════════════════
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

// ── Helpers ──────────────────────────────────────────────────────
function fmtMonth(key) {
  if (!key) return "";
  const [y, m] = key.split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[parseInt(m, 10) - 1]}-${y}`;
}

function fmtAmt(n) {
  return "Rs." + Number(n || 0).toLocaleString("en-IN");
}

function fmtDate(ts) {
  if (!ts) return "—";
  let d;
  if (ts.toDate) d = ts.toDate();
  else if (ts.seconds) d = new Date(ts.seconds * 1000);
  else d = new Date(ts);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// Calculates ageing text: "On Time", "X days late", "X months Y days late",
// or "X months Y days overdue" (red) for unpaid months.
function calcAgeing(mk, paidDateStr, paidTimestamp) {
  const dueDate = new Date(`${mk}-10`); // due on 10th of the month
  if (!paidDateStr && !paidTimestamp) {
    // Not paid — calculate how many days overdue from 10th to today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - dueDate) / (1000 * 60 * 60 * 24));
    if (diffDays <= 0) return { text: "Not Paid", color: [180, 0, 0] }; // due date not yet crossed
    const months = Math.floor(diffDays / 30);
    const days = diffDays % 30;
    let text;
    if (months === 0) text = `${days} days overdue`;
    else if (days === 0) text = `${months} month${months > 1 ? "s" : ""} overdue`;
    else text = `${months} month${months > 1 ? "s" : ""} ${days} days overdue`;
    return { text, color: [180, 0, 0] };
  }

  let paidD;
  if (paidTimestamp) {
    if (paidTimestamp.toDate) paidD = paidTimestamp.toDate();
    else if (paidTimestamp.seconds) paidD = new Date(paidTimestamp.seconds * 1000);
    else paidD = new Date(paidTimestamp);
  } else {
    paidD = new Date(paidDateStr);
  }
  if (isNaN(paidD)) return { text: "—", color: [40, 40, 40] };

  const diffDays = Math.floor((paidD - dueDate) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return { text: "On Time", color: [0, 120, 0] };

  const months = Math.floor(diffDays / 30);
  const days = diffDays % 30;
  let text;
  if (months === 0) text = `${days} days late`;
  else if (days === 0) text = `${months} month${months > 1 ? "s" : ""} late`;
  else text = `${months} month${months > 1 ? "s" : ""} ${days} days late`;
  return { text, color: [180, 100, 0] };
}

// Returns FY months from April to current month (or to exitMonth if provided)
function getFYMonths(exitMonthKey) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const fyStartYear = m >= 4 ? y : y - 1;
  const todayKey = `${y}-${String(m).padStart(2, "0")}`;
  const endKey = exitMonthKey || todayKey;

  const months = [];
  let cy = fyStartYear, cm = 4;
  while (true) {
    const key = `${cy}-${String(cm).padStart(2, "0")}`;
    if (key > endKey) break;
    months.push(key);
    cm++;
    if (cm > 12) { cm = 1; cy++; }
  }
  return months;
}

// Returns FY months from entryMonth to exitMonth (for tenant report)
function getTenantMonths(entryDateStr, exitDateStr) {
  if (!entryDateStr) return [];
  const entry = new Date(entryDateStr);
  const exit  = exitDateStr ? new Date(exitDateStr) : new Date();

  const entryKey = `${entry.getFullYear()}-${String(entry.getMonth() + 1).padStart(2, "0")}`;
  const exitKey  = `${exit.getFullYear()}-${String(exit.getMonth() + 1).padStart(2, "0")}`;

  const months = [];
  let d = new Date(entry.getFullYear(), entry.getMonth(), 1);
  while (true) {
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (key > exitKey) break;
    months.push(key);
    d.setMonth(d.getMonth() + 1);
  }
  return months;
}

// ── Draw Letterhead ───────────────────────────────────────────────
// Logo displayed as full-width banner across top of page (like a letterhead).
// Returns the Y position after the letterhead so content starts below it.
function drawLetterheadBackground(doc, settings) {
  if (!settings?.letterheadImage) return;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  try {
    // Draw full letterhead as page background at native A4 proportions
    // Original image is 1130x1600px — same ratio as A4 (210x297mm)
    doc.addImage(settings.letterheadImage, "JPEG", 0, 0, pageW, pageH, undefined, "FAST");
  } catch (e) {
    // Letterhead failed to load - skip silently
  }
}

function drawLetterhead(doc, settings) {
  drawLetterheadBackground(doc, settings);
  // Content starts at 68mm from top — below the letterhead header area
  return 68;
}

// ── Build Maintenance Rows ────────────────────────────────────────
// Returns array of table rows for maintenance, month by month.
// Columns: Month | Brought Fwd | This Month | Total | Paid | Paid Date | Balance | Ageing
function buildMaintenanceRows(months, payments, settings, flat) {
  const monthlyRate = flat?.maintenanceRate || settings?.maintenanceAmt || 1500;
  let carryForward = 0;
  const rows = [];
  let totalBroughtFwd = 0, totalThisMonth = 0, totalTotal = 0, totalPaid = 0, totalBalance = 0;

  for (const mk of months) {
    const payment = payments.find(
      p => p.month === mk &&
           p.status === "confirmed" &&
           (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
    );

    const thisMonth = monthlyRate;
    const total = carryForward + thisMonth;
    const paid = payment ? Number(payment.amount || 0) : 0;
    const balance = total - paid;
    const paidDate = payment ? fmtDate(payment.confirmedAt || payment.declaredAt) : "—";

    const ageing = payment
      ? calcAgeing(mk, payment.paidDate, payment.confirmedAt || payment.declaredAt)
      : calcAgeing(mk, null, null); // shows overdue days in red for unpaid

    totalBroughtFwd += carryForward;
    totalThisMonth += thisMonth;
    totalTotal += total;
    totalPaid += paid;
    totalBalance += balance;

    rows.push([
      fmtMonth(mk),
      fmtAmt(carryForward),
      fmtAmt(thisMonth),
      fmtAmt(total),
      paid > 0 ? fmtAmt(paid) : "—",
      paidDate,
      fmtAmt(balance),
      ageing.text,
    ]);

    carryForward = balance > 0 ? balance : 0;
  }

  // Totals row — only Paid total and final outstanding balance
  rows.push([
    "Total",
    "—",
    "—",
    "—",
    totalPaid > 0 ? fmtAmt(totalPaid) : "—",
    "—",
    fmtAmt(carryForward),
    "—",
  ]);

  return rows;
}

// ── Build Water Rows ─────────────────────────────────────────────
// Returns array of table rows for water bills, month by month.
function buildWaterRows(months, waterBills) {
  let carryForward = 0;
  const rows = [];
  let totalBroughtFwd = 0, totalThisMonth = 0, totalTotal = 0, totalPaid = 0, totalBalance = 0;

  for (const mk of months) {
    const bill = waterBills.find(w => w.month === mk);
    const thisMonth = bill ? Number(bill.billAmount || bill.totalDue || 0) : 0;
    const total = carryForward + thisMonth;
    // Use the actual amount paid where recorded, falling back to the billed
    // amount only if paidAmount is missing — keeps this in sync with the
    // Income & Expense report's water income calculation.
    const paid = (bill && bill.paid) ? Number(bill.paidAmount || bill.billAmount || bill.totalDue || 0) : 0;
    const balance = total - paid;
    const paidDate = bill?.paidDate ? fmtDate(bill.paidDate) : "—";

    // Only show ageing if there is actually something due (thisMonth > 0 or carryForward > 0)
    const hasDue = total > 0;
    const ageing = !hasDue
      ? { text: "—", color: [40, 40, 40] }
      : (bill?.paid && bill?.paidDate)
        ? calcAgeing(mk, bill.paidDate, null)
        : calcAgeing(mk, null, null);

    totalBroughtFwd += carryForward;
    totalThisMonth += thisMonth;
    totalTotal += total;
    totalPaid += paid;
    totalBalance += balance;

    rows.push([
      fmtMonth(mk),
      fmtAmt(carryForward),
      fmtAmt(thisMonth),
      fmtAmt(total),
      paid > 0 ? fmtAmt(paid) : "—",
      paidDate,
      fmtAmt(balance),
      ageing.text,
    ]);

    carryForward = balance > 0 ? balance : 0;
  }

  // Totals row — only Paid total and final outstanding balance
  rows.push([
    "Total",
    "—",
    "—",
    "—",
    totalPaid > 0 ? fmtAmt(totalPaid) : "—",
    "—",
    fmtAmt(carryForward),
    "—",
  ]);

  return rows;
}

// ── Build Meter Reading Rows ─────────────────────────────────────
function buildMeterRows(months, waterBills, meterReadings) {
  const rows = [];
  let totalUnits = 0, totalBill = 0;

  for (const mk of months) {
    const meter = meterReadings ? meterReadings.find(r => r.month === mk) : null;
    const bill = waterBills.find(w => w.month === mk);
    const opening = meter?.previousReading != null ? String(meter.previousReading) : "—";
    const closing = meter?.reading != null ? String(meter.reading) : "—";
    const units = meter?.litersUsed != null ? meter.litersUsed : null;
    const billAmt = bill ? Number(bill.billAmount || bill.totalDue || 0) : 0;

    if (units != null) totalUnits += units;
    totalBill += billAmt;

    rows.push([
      fmtMonth(mk),
      opening,
      closing,
      units != null ? String(units) : "—",
      billAmt > 0 ? fmtAmt(billAmt) : "—",
    ]);
  }

  // Totals row
  rows.push([
    "Total",
    "",
    "",
    totalUnits > 0 ? String(totalUnits) : "—",
    totalBill > 0 ? fmtAmt(totalBill) : "—",
  ]);

  return rows;
}

// ── Build Other Payments Rows ────────────────────────────────────
function buildOtherRows(months, adhocPayments) {
  const rows = [];
  for (const a of adhocPayments) {
    let eventMonth = null;
    if (a.addedAt) {
      const d = a.addedAt.toDate ? a.addedAt.toDate() : new Date(a.addedAt.seconds * 1000);
      eventMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    } else if (a.paidDate) {
      eventMonth = a.paidDate.slice(0, 7);
    }
    if (eventMonth && months.includes(eventMonth)) {
      rows.push([
        fmtMonth(eventMonth),
        a.eventTitle || "Society Work",
        "Society Work",
        fmtAmt(a.amount),
        a.paid ? "Paid" : "Pending",
      ]);
    }
  }
  return rows;
}

// ── Common table style ───────────────────────────────────────────
const TABLE_STYLES = {
  headStyles: {
    fillColor: [30, 30, 60],
    textColor: [200, 200, 255],
    fontSize: 8,
    fontStyle: "bold",
    halign: "right",
  },
  bodyStyles: {
    fontSize: 8,
    textColor: [40, 40, 40],
    halign: "right",
  },
  columnStyles: {
    0: { halign: "left" }, // Month column left-aligned
  },
  alternateRowStyles: { fillColor: [245, 245, 255] },
  margin: { left: 10, right: 10 },
};

// ── Summary Cards row ────────────────────────────────────────────
function drawSummaryCards(doc, startY, totalPaid, totalOutstanding, onTimeCount, totalMonths) {
  const pageW = doc.internal.pageSize.getWidth();
  const cardW = (pageW - 28) / 3;
  let x = 10;

  const cards = [
    { label: "Total Paid (YTD)", value: fmtAmt(totalPaid),       color: [0, 120, 0] },
    { label: "Outstanding",      value: fmtAmt(totalOutstanding), color: [180, 0, 0] },
    { label: "On Time Payments", value: `${onTimeCount} / ${totalMonths}`, color: [0, 60, 180] },
  ];

  cards.forEach(card => {
    doc.setDrawColor(200, 200, 220);
    doc.setFillColor(248, 248, 255);
    doc.roundedRect(x, startY, cardW, 16, 2, 2, "FD");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 120);
    doc.text(card.label, x + cardW / 2, startY + 5, { align: "center" });
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...card.color);
    doc.text(card.value, x + cardW / 2, startY + 12, { align: "center" });
    x += cardW + 4;
  });

  return startY + 22;
}

// ════════════════════════════════════════════════════════════════
// MAIN EXPORT 1 — generateFlatOwnerReport
// Call this from PassbookScreen or ReportsScreen for flat owners.
// ════════════════════════════════════════════════════════════════
export async function generateFlatOwnerReport({
  settings,
  flat,          // { flatId, ownerName, phone, email, maintenanceRate }
  payments,      // all payments for this flat
  waterBills,    // all water bills for this flat
  meterReadings, // all meter readings for this flat
  adhocPayments, // all society work payments for this flat
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Draw letterhead background on every new page automatically
  doc.internal.events.subscribe("addPage", () => drawLetterheadBackground(doc, settings));

  // 1. Letterhead
  let y = drawLetterhead(doc, settings);

  // 2. Report title + flat details
  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const reportTitle = `Flat Owner Statement — FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 80);
  doc.text(reportTitle, pageW / 2, y, { align: "center" });
  y += 6;

  // Generated date
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, pageW - 10, y, { align: "right" });
  y += 2;

  // Flat info row — single line
  doc.setDrawColor(200, 200, 220);
  doc.setFillColor(240, 240, 255);
  doc.rect(10, y, pageW - 20, 8, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text(`Flat: ${flat.flatId}   |   Owner: ${flat.ownerName || ""}`, 14, y + 5);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  const contactParts = [];
  if (flat.phone) contactParts.push(`Phone: ${flat.phone}`);
  if (flat.email) contactParts.push(`Email: ${flat.email}`);
  if (contactParts.length > 0) doc.text(contactParts.join("   |   "), pageW - 14, y + 5, { align: "right" });
  y += 12;

  // 3. FY months
  const months = getFYMonths();

  // 4. Maintenance table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text("Maintenance", 10, y);
  y += 3;

  const maintRows = buildMaintenanceRows(months, payments, settings, flat);
  autoTable(doc, {
    startY: y,
    head: [["Month", "Brought Fwd", "This Month", "Total", "Paid", "Paid Date", "Balance", "Ageing"]],
    body: maintRows,
    ...TABLE_STYLES,
    didParseCell: (data) => {
      const isTotal = data.row.index === maintRows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 220, 240];
      }
      if (!isTotal && data.section === "body") {
        if (data.column.index === 6) {
          const val = data.cell.raw;
          if (val !== "Rs.0") data.cell.styles.textColor = [180, 0, 0];
          else data.cell.styles.textColor = [0, 120, 0];
        }
        if (data.column.index === 4 && data.cell.raw !== "—") {
          data.cell.styles.textColor = [0, 120, 0];
        }
        if (data.column.index === 7) {
          const val = data.cell.raw;
          if (val === "On Time") data.cell.styles.textColor = [0, 120, 0];
          else if (val === "Not Paid" || val.includes("overdue")) data.cell.styles.textColor = [180, 0, 0];
          else if (val !== "—") data.cell.styles.textColor = [180, 100, 0];
        }
      }
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  // 6. Water table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text("Water Bills", 10, y);
  y += 3;

  const waterRows = buildWaterRows(months, waterBills);
  autoTable(doc, {
    startY: y,
    head: [["Month", "Brought Fwd", "This Month", "Total", "Paid", "Paid Date", "Balance", "Ageing"]],
    body: waterRows,
    ...TABLE_STYLES,
    didParseCell: (data) => {
      const isTotal = data.row.index === waterRows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 220, 240];
      }
      if (!isTotal && data.section === "body") {
        if (data.column.index === 6) {
          const val = data.cell.raw;
          if (val !== "Rs.0") data.cell.styles.textColor = [180, 0, 0];
          else data.cell.styles.textColor = [0, 120, 0];
        }
        if (data.column.index === 4 && data.cell.raw !== "—") {
          data.cell.styles.textColor = [0, 120, 0];
        }
        if (data.column.index === 7) {
          const val = data.cell.raw;
          if (val === "On Time") data.cell.styles.textColor = [0, 120, 0];
          else if (val === "Not Paid" || val.includes("overdue")) data.cell.styles.textColor = [180, 0, 0];
          else if (val !== "—") data.cell.styles.textColor = [180, 100, 0];
        }
      }
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  // 7. Meter readings table
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text("Water Meter Readings", 10, y);
  y += 3;

  const meterRows = buildMeterRows(months, waterBills, meterReadings);
  autoTable(doc, {
    startY: y,
    head: [["Month", "Opening", "Closing", "Units Used", "Bill Amount"]],
    body: meterRows,
    ...TABLE_STYLES,
    didParseCell: (data) => {
      const isTotal = data.row.index === meterRows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 220, 240];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 6;

  // 8. Other payments table (adhoc/society work)
  const otherRows = buildOtherRows(months, adhocPayments);
  if (otherRows.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 80);
    doc.text("Other Payments (Society Work / Adhoc)", 10, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Month", "Description", "Type", "Amount", "Status"]],
      body: otherRows,
      ...TABLE_STYLES,
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "left" },
      },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // 9. Footer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "This is a computer-generated statement. For queries contact the Management Committee.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 8,
    { align: "center" }
  );
  doc.text(
    "Generated via Adyantam Building Circle\u00ae",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 4,
    { align: "center" }
  );

  // Save / download
  const fileName = `Flat${flat.flatId}_Statement_${fyStartYear}-${String(fyStartYear + 1).slice(-2)}.pdf`;
  doc.save(fileName);
}

// ════════════════════════════════════════════════════════════════
// MAIN EXPORT 2 — generateTenantReport
// Covers tenant stay period. Shows sections based on payment flags.
// ════════════════════════════════════════════════════════════════
export async function generateTenantReport({
  settings,
  flat,            // { flatId, ownerName }
  tenant,          // { name, phone, email, entryDate, exitDate, paysMaintenance, paysWater, familyMembers }
  payments,        // all maintenance payments for this flat
  waterBills,      // all water bills for this flat
  meterReadings,   // all meter readings for this flat
  adhocPayments,   // all society work payments for this flat
  pocketExpenses = [], // pocket expenses submitted by tenant/family
}) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  // Draw letterhead background on every new page automatically
  doc.internal.events.subscribe("addPage", () => drawLetterheadBackground(doc, settings));

  // 1. Letterhead
  let y = drawLetterhead(doc, settings);

  // 2. Report title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 80);
  doc.text("Tenant Statement", pageW / 2, y, { align: "center" });
  y += 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, pageW - 10, y, { align: "right" });
  y += 2;

  // 3. Flat + tenant info — single row each
  doc.setDrawColor(200, 200, 220);
  doc.setFillColor(240, 240, 255);
  doc.rect(10, y, pageW - 20, 14, "FD");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text(`Flat: ${flat.flatId}   |   Owner: ${flat.ownerName || ""}`, 14, y + 5);
  doc.text(`Tenant: ${tenant.name || ""}`, 14, y + 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(60, 60, 60);
  const tenantContactParts = [];
  if (tenant.phone) tenantContactParts.push(`Phone: ${tenant.phone}`);
  if (tenantContactParts.length > 0) doc.text(tenantContactParts.join("   |   "), pageW - 14, y + 5, { align: "right" });
  doc.text(`Stay: ${tenant.entryDate || "—"} to ${tenant.exitDate || "Active"}`, pageW - 14, y + 10, { align: "right" });
  y += 18;

  // 4. Months covered during tenant's stay
  const months = getTenantMonths(tenant.entryDate, tenant.exitDate);

  // Build list of names for adhoc payment matching — tenant + family members
  const tenantNames = [
    (tenant.name || "").toLowerCase(),
    ...(tenant.familyMembers || []).map(m => (m.name || "").toLowerCase()),
  ].filter(Boolean);

  // Helper to render maintenance/water didParseCell colour logic
  function makeDidParseCell(rows) {
    return (data) => {
      const isTotal = data.row.index === rows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 220, 240];
      }
      if (!isTotal && data.section === "body") {
        if (data.column.index === 6) {
          const val = data.cell.raw;
          if (val !== "Rs.0") data.cell.styles.textColor = [180, 0, 0];
          else data.cell.styles.textColor = [0, 120, 0];
        }
        if (data.column.index === 4 && data.cell.raw !== "—") {
          data.cell.styles.textColor = [0, 120, 0];
        }
        if (data.column.index === 7) {
          const val = data.cell.raw;
          if (val === "On Time") data.cell.styles.textColor = [0, 120, 0];
          else if (val === "Not Paid" || val.includes("overdue")) data.cell.styles.textColor = [180, 0, 0];
          else if (val !== "—") data.cell.styles.textColor = [180, 100, 0];
        }
      }
    };
  }

  // 5. Maintenance table — only if tenant pays maintenance
  if (tenant.paysMaintenance) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 80);
    doc.text("Maintenance", 10, y);
    y += 3;

    const maintRows = buildMaintenanceRows(months, payments, settings, flat);
    autoTable(doc, {
      startY: y,
      head: [["Month", "Brought Fwd", "This Month", "Total", "Paid", "Paid Date", "Balance", "Ageing"]],
      body: maintRows,
      ...TABLE_STYLES,
      didParseCell: makeDidParseCell(maintRows),
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // 6. Water Bills + Meter Readings — only if tenant pays water
  if (tenant.paysWater) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 80);
    doc.text("Water Bills", 10, y);
    y += 3;

    const waterRows = buildWaterRows(months, waterBills);
    autoTable(doc, {
      startY: y,
      head: [["Month", "Brought Fwd", "This Month", "Total", "Paid", "Paid Date", "Balance", "Ageing"]],
      body: waterRows,
      ...TABLE_STYLES,
      didParseCell: makeDidParseCell(waterRows),
    });
    y = doc.lastAutoTable.finalY + 6;

    // Meter readings
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 80);
    doc.text("Water Meter Readings", 10, y);
    y += 3;

    const meterRows = buildMeterRows(months, waterBills, meterReadings);
    autoTable(doc, {
      startY: y,
      head: [["Month", "Opening", "Closing", "Units Used", "Bill Amount"]],
      body: meterRows,
      ...TABLE_STYLES,
      didParseCell: (data) => {
        const isTotal = data.row.index === meterRows.length - 1;
        if (isTotal) {
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.fillColor = [220, 220, 240];
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // 7. Other payments — filter by tenant name or family member names via paidBy
  const tenantAdhoc = adhocPayments.filter(a => {
    if (!a.paidBy) return false;
    return tenantNames.includes(a.paidBy.toLowerCase());
  });
  const otherRows = buildOtherRows(months, tenantAdhoc);
  if (otherRows.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 80);
    doc.text("Other Payments (Society Work / Adhoc)", 10, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Month", "Description", "Type", "Amount", "Status"]],
      body: otherRows,
      ...TABLE_STYLES,
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "left" },
      },
    });
  }

  // 8. Pocket Expenses — submitted by tenant or family members
  const tenantMonths = months; // already computed above
  const pocketRows = pocketExpenses
    .filter(e => {
      if (!e.month) return false;
      return tenantMonths.includes(e.month);
    })
    .map(e => [
      fmtMonth(e.month),
      e.description || "Pocket Expense",
      e.approvalStatus === "paid" ? "Reimbursed"
        : e.approvalStatus === "approved" ? "Approved"
        : e.approvalStatus === "pending_president" ? "Pending Approval"
        : "Rejected",
      fmtAmt(e.amount),
      e.addedBy || "—",
    ]);

  if (pocketRows.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 80);
    doc.text("Pocket Expenses (Reimbursable)", 10, y);
    y += 3;

    autoTable(doc, {
      startY: y,
      head: [["Month", "Description", "Status", "Amount", "Submitted By"]],
      body: pocketRows,
      ...TABLE_STYLES,
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "left" },
        4: { halign: "left" },
      },
      didParseCell: (data) => {
        if (data.section === "body" && data.column.index === 2) {
          const val = data.cell.raw;
          if (val === "Reimbursed") data.cell.styles.textColor = [0, 120, 0];
          else if (val === "Approved") data.cell.styles.textColor = [0, 80, 180];
          else if (val === "Pending Approval") data.cell.styles.textColor = [180, 100, 0];
          else if (val === "Rejected") data.cell.styles.textColor = [180, 0, 0];
        }
      },
    });
    y = doc.lastAutoTable.finalY + 6;
  }

  // 10. Footer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "This is a computer-generated statement. For queries contact the Management Committee.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 8,
    { align: "center" }
  );
  doc.text(
    "Generated via Adyantam Building Circle\u00ae",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 4,
    { align: "center" }
  );

  const fileName = `Flat${flat.flatId}_Tenant_${tenant.name?.replace(/\s+/g, "_") || "Report"}.pdf`;
  doc.save(fileName);
}

// ════════════════════════════════════════════════════════════════
// MAIN EXPORT 3 — generateOutstandingDuesReport  (NEW DESIGN)
// Society-level report: TWO separate tables —
//   Table 1: Maintenance Outstanding (Flat | Owner | month-by-month | YTD Total)
//   Table 2: Water Outstanding       (Flat | Owner | month-by-month | YTD Total)
// Auto-switches to Landscape if there are too many month columns to
// fit comfortably in Portrait. Uses the same letterhead as other reports.
// ════════════════════════════════════════════════════════════════
export async function generateOutstandingDuesReport({ settings, flats, allPayments, allWaterBills }) {
  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = getFYMonths();

  // Decide Portrait vs Landscape based on column count.
  // Columns = Flat + one per month + YTD Total  (Owner column removed per Vijay's request)
  const colCount = months.length + 2;
  const orientation = colCount > 8 ? "landscape" : "portrait";

  const doc = new jsPDF({ orientation, unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.internal.events.subscribe("addPage", () => drawLetterheadBackground(doc, settings));
  let y = drawLetterhead(doc, settings);

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 80);
  doc.text(`Outstanding Dues Report — FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`, pageW / 2, y, { align: "center" });
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, pageW - 10, y, { align: "right" });
  y += 6;

  // Pre-calculate month-wise pending balance for each flat, for both
  // Maintenance and Water, so both tables can reuse the same numbers.
  const maintByFlat = {};   // flatId -> { perMonth: [...], ytd }
  const waterByFlat = {};   // flatId -> { perMonth: [...], ytd }

  for (const flat of flats) {
    const monthlyRate = flat.maintenanceRate || settings?.maintenanceAmt || 1500;
    const flatPayments = allPayments.filter(p => p.flatId === flat.flatId);
    const flatWater = allWaterBills.filter(w => w.flatId === flat.flatId);

    // Maintenance: pending balance for each individual month
    let maintCarry = 0;
    const maintPerMonth = [];
    for (const mk of months) {
      const payment = flatPayments.find(p =>
        p.month === mk && p.status === "confirmed" &&
        (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
      );
      const total = maintCarry + monthlyRate;
      const paid = payment ? Number(payment.amount || 0) : 0;
      maintCarry = Math.max(total - paid, 0);
      maintPerMonth.push(maintCarry);
    }
    maintByFlat[flat.flatId] = { perMonth: maintPerMonth, ytd: maintCarry };

    // Water: pending balance for each individual month
    let waterCarry = 0;
    const waterPerMonth = [];
    for (const mk of months) {
      const bill = flatWater.find(w => w.month === mk);
      const thisMonth = bill ? Number(bill.billAmount || bill.totalDue || 0) : 0;
      const total = waterCarry + thisMonth;
      // Prefer the actual paid amount, same fallback order used everywhere else
      const paid = bill?.paid ? Number(bill.paidAmount || bill.billAmount || bill.totalDue || 0) : 0;
      waterCarry = Math.max(total - paid, 0);
      waterPerMonth.push(waterCarry);
    }
    waterByFlat[flat.flatId] = { perMonth: waterPerMonth, ytd: waterCarry };
  }

  // Shared column styling: Flat left aligned, rest right aligned
  function buildColumnStyles() {
    const styles = { 0: { halign: "left" } };
    for (let i = 1; i < colCount; i++) styles[i] = { halign: "right" };
    return styles;
  }

  // Smaller font + tighter row height than the default TABLE_STYLES,
  // so both tables fit on one page for the first several months.
  const COMPACT_STYLES = {
    headStyles: { fillColor: [30, 30, 60], textColor: [200, 200, 255], fontSize: 7, fontStyle: "bold", halign: "right", cellPadding: 1.5 },
    bodyStyles: { fontSize: 7, textColor: [40, 40, 40], halign: "right", cellPadding: 1.2 },
    alternateRowStyles: { fillColor: [245, 245, 255] },
    margin: { left: 10, right: 10 },
  };

  // Shared row-coloring: YTD Total column (last column) red if pending, green if zero
  function buildDidParseCell(rowsRef) {
    return (data) => {
      const isTotal = data.row.index === rowsRef.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 220, 240];
        return;
      }
      if (data.section === "body" && data.column.index === colCount - 1) {
        const raw = data.cell.raw;
        data.cell.styles.textColor = (raw !== "Rs.0") ? [180, 0, 0] : [0, 120, 0];
      }
    };
  }

  const monthHeads = months.map(mk => fmtMonth(mk));

  // ── TABLE 1: MAINTENANCE OUTSTANDING ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 80);
  doc.text("Maintenance Outstanding", 10, y);
  y += 4;

  const maintRows = [];
  let grandMaintByMonth = new Array(months.length).fill(0);
  let grandMaintYtd = 0;

  for (const flat of flats) {
    const data = maintByFlat[flat.flatId];
    data.perMonth.forEach((v, i) => { grandMaintByMonth[i] += v; });
    grandMaintYtd += data.ytd;
    maintRows.push([
      flat.flatId,
      ...data.perMonth.map(v => fmtAmt(v)),
      fmtAmt(data.ytd),
    ]);
  }
  maintRows.push([
    "Total",
    ...grandMaintByMonth.map(v => fmtAmt(v)),
    fmtAmt(grandMaintYtd),
  ]);

  autoTable(doc, {
    startY: y,
    head: [["Flat", ...monthHeads, "Maintenance Due (YTD)"]],
    body: maintRows,
    ...COMPACT_STYLES,
    columnStyles: buildColumnStyles(),
    didParseCell: buildDidParseCell(maintRows),
  });
  y = doc.lastAutoTable.finalY + 10;

  // Add new page if Water table won't fit below
  if (y > doc.internal.pageSize.getHeight() - 40) {
    doc.addPage();
    y = drawLetterhead(doc, settings);
  }

  // ── TABLE 2: WATER OUTSTANDING ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 80);
  doc.text("Water Outstanding", 10, y);
  y += 4;

  const waterRows = [];
  let grandWaterByMonth = new Array(months.length).fill(0);
  let grandWaterYtd = 0;

  for (const flat of flats) {
    const data = waterByFlat[flat.flatId];
    data.perMonth.forEach((v, i) => { grandWaterByMonth[i] += v; });
    grandWaterYtd += data.ytd;
    waterRows.push([
      flat.flatId,
      ...data.perMonth.map(v => fmtAmt(v)),
      fmtAmt(data.ytd),
    ]);
  }
  waterRows.push([
    "Total",
    ...grandWaterByMonth.map(v => fmtAmt(v)),
    fmtAmt(grandWaterYtd),
  ]);

  autoTable(doc, {
    startY: y,
    head: [["Flat", ...monthHeads, "Water Due (YTD)"]],
    body: waterRows,
    ...COMPACT_STYLES,
    columnStyles: buildColumnStyles(),
    didParseCell: buildDidParseCell(waterRows),
  });
  y = doc.lastAutoTable.finalY + 8;

  // Footer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "This is a computer-generated statement. For queries contact the Management Committee.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 8,
    { align: "center" }
  );
  doc.text(
    "Generated via Adyantam Building Circle\u00ae",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 4,
    { align: "center" }
  );

  doc.save(`OutstandingDues_${fyStartYear}-${String(fyStartYear + 1).slice(-2)}.pdf`);
}

// ════════════════════════════════════════════════════════════════
// LEGACY (kept but DISCONNECTED — not exported, not called anywhere)
// generateOutstandingDuesReport_OLD — original combined-table version:
// Flat | Owner | Maintenance Due | Water Due | Total Outstanding
// Kept only for reference, in case the new report design ever needs
// to be rolled back.
// ════════════════════════════════════════════════════════════════
async function generateOutstandingDuesReport_OLD({ settings, flats, allPayments, allWaterBills }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.internal.events.subscribe("addPage", () => drawLetterheadBackground(doc, settings));
  let y = drawLetterhead(doc, settings);

  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = getFYMonths();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 80);
  doc.text(`Outstanding Dues Report — FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`, pageW / 2, y, { align: "center" });
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, pageW - 10, y, { align: "right" });
  y += 6;

  const rows = [];
  let grandMaint = 0, grandWater = 0, grandTotal = 0;

  for (const flat of flats) {
    const monthlyRate = flat.maintenanceRate || settings?.maintenanceAmt || 1500;
    const flatPayments = allPayments.filter(p => p.flatId === flat.flatId);
    const flatWater = allWaterBills.filter(w => w.flatId === flat.flatId);

    let maintCarry = 0;
    for (const mk of months) {
      const payment = flatPayments.find(p =>
        p.month === mk && p.status === "confirmed" &&
        (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
      );
      const total = maintCarry + monthlyRate;
      const paid = payment ? Number(payment.amount || 0) : 0;
      maintCarry = Math.max(total - paid, 0);
    }

    let waterCarry = 0;
    for (const mk of months) {
      const bill = flatWater.find(w => w.month === mk);
      const thisMonth = bill ? Number(bill.billAmount || bill.totalDue || 0) : 0;
      const total = waterCarry + thisMonth;
      // Prefer the actual paid amount, same fallback order used everywhere else
      const paid = bill?.paid ? Number(bill.paidAmount || bill.billAmount || bill.totalDue || 0) : 0;
      waterCarry = Math.max(total - paid, 0);
    }

    const totalOutstanding = maintCarry + waterCarry;
    grandMaint += maintCarry;
    grandWater += waterCarry;
    grandTotal += totalOutstanding;

    rows.push([
      flat.flatId,
      flat.ownerName || "—",
      fmtAmt(maintCarry),
      fmtAmt(waterCarry),
      fmtAmt(totalOutstanding),
    ]);
  }

  rows.push(["Total", "", fmtAmt(grandMaint), fmtAmt(grandWater), fmtAmt(grandTotal)]);

  autoTable(doc, {
    startY: y,
    head: [["Flat", "Owner", "Maintenance Due", "Water Due", "Total Outstanding"]],
    body: rows,
    ...TABLE_STYLES,
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "left" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
    },
    didParseCell: (data) => {
      const isTotal = data.row.index === rows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 220, 240];
      }
      if (!isTotal && data.section === "body") {
        if (data.column.index === 4) {
          const raw = data.cell.raw;
          if (raw !== "Rs.0") data.cell.styles.textColor = [180, 0, 0];
          else data.cell.styles.textColor = [0, 120, 0];
        }
      }
    },
  });

  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "This is a computer-generated statement. For queries contact the Management Committee.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 8,
    { align: "center" }
  );
  doc.text(
    "Generated via Adyantam Building Circle\u00ae",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 4,
    { align: "center" }
  );

  doc.save(`OutstandingDues_${fyStartYear}-${String(fyStartYear + 1).slice(-2)}.pdf`);
}

// ════════════════════════════════════════════════════════════════
// MAIN EXPORT 4 — generateIncomeExpenseReport
// Society-level FY Income & Expense Statement
// Two month-by-month tables: Maintenance Fund + Water Revolving Fund
// Followed by Cash Available and Pending Collections summary boxes
// ════════════════════════════════════════════════════════════════
export async function generateIncomeExpenseReport({ settings, flats, allPayments, allWaterBills, allExpenses, maintenanceIncome }) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.internal.events.subscribe("addPage", () => drawLetterheadBackground(doc, settings));
  let y = drawLetterhead(doc, settings);

  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = getFYMonths();

  // Opening balances from settings
  const maintOpening = Number(settings?.maintOpeningBalance || 0);
  const waterOpening = Number(settings?.waterDeposit || 0);

  // ── Title ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 80);
  doc.text(`Income & Expense Statement — FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`, pageW / 2, y, { align: "center" });
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, pageW - 10, y, { align: "right" });
  y += 8;

  // ── SECTION 1: MAINTENANCE FUND ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text("Maintenance Fund", 10, y);
  y += 3;

  // Build ad-hoc income lookup by month
  const adhocByMonth = {};
  for (const inc of (maintenanceIncome || [])) {
    let mk = null;
    if (inc.date) {
      const d = inc.date.toDate ? inc.date.toDate() : new Date(inc.date.seconds ? inc.date.seconds * 1000 : inc.date);
      if (!isNaN(d)) mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    } else if (inc.month) {
      mk = inc.month;
    }
    if (mk) {
      if (!adhocByMonth[mk]) adhocByMonth[mk] = 0;
      adhocByMonth[mk] += Number(inc.amount || 0);
    }
  }

  const maintRows = [];
  let maintCarry = maintOpening;
  let totalMaintIncome = 0, totalMaintExpenses = 0;

  for (const mk of months) {
    // Income: confirmed maintenance payments + ad-hoc income this month
    const monthPayments = allPayments.filter(p =>
      p.month === mk && p.status === "confirmed" &&
      (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
    );
    const maintCollected = monthPayments.reduce((s, p) => s + Number(p.amount || 0), 0);
    const adhocCollected = adhocByMonth[mk] || 0;
    const income = maintCollected + adhocCollected;

    // Expenses: approved, excluding Water Tanker category
    const monthExpenses = (allExpenses || []).filter(e =>
      e.month === mk &&
      e.approvalStatus === "approved" &&
      e.category !== "Water Tanker"
    );
    const expenses = monthExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);

    const balance = maintCarry + income - expenses;
    totalMaintIncome += income;
    totalMaintExpenses += expenses;

    maintRows.push([
      fmtMonth(mk),
      fmtAmt(maintCarry),
      fmtAmt(income),
      fmtAmt(expenses),
      fmtAmt(balance),
    ]);

    maintCarry = balance;
  }

  // Totals row
  maintRows.push([
    "Total",
    "—",
    fmtAmt(totalMaintIncome),
    fmtAmt(totalMaintExpenses),
    fmtAmt(maintCarry),
  ]);

  autoTable(doc, {
    startY: y,
    head: [["Month", "Brought Fwd", "Income", "Expenses", "Balance"]],
    body: maintRows,
    ...TABLE_STYLES,
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
    },
    didParseCell: (data) => {
      const isTotal = data.row.index === maintRows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 240, 220];
      }
      if (!isTotal && data.section === "body") {
        if (data.column.index === 2) data.cell.styles.textColor = [0, 120, 0];
        if (data.column.index === 3) data.cell.styles.textColor = [180, 0, 0];
      }
    },
  });
  y = doc.lastAutoTable.finalY + 10;

  // ── SECTION 2: WATER REVOLVING FUND ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text("Water Revolving Fund", 10, y);
  y += 3;

  const waterRows = [];
  let waterCarry = waterOpening;
  let totalTankerCost = 0, totalWaterIncome = 0, totalWaterPending = 0;

  for (const mk of months) {
    // Tanker cost: approved Water Tanker expenses this month
    const tankerExpenses = (allExpenses || []).filter(e =>
      e.month === mk &&
      e.approvalStatus === "approved" &&
      e.category === "Water Tanker"
    );
    const tankerCost = tankerExpenses.reduce((s, e) => s + Number(e.amount || 0), 0);

    // Water bills paid this month
    const paidBills = allWaterBills.filter(w => w.month === mk && w.paid);
    const waterIncome = paidBills.reduce((s, w) => s + Number(w.paidAmount || w.billAmount || w.totalDue || 0), 0);

    // Water bills unpaid this month
    const unpaidBills = allWaterBills.filter(w => w.month === mk && !w.paid);
    const waterPending = unpaidBills.reduce((s, w) => s + Number(w.billAmount || w.totalDue || 0), 0);

    const balance = waterCarry - tankerCost + waterIncome;
    totalTankerCost += tankerCost;
    totalWaterIncome += waterIncome;
    totalWaterPending += waterPending;

    waterRows.push([
      fmtMonth(mk),
      fmtAmt(waterCarry),
      fmtAmt(tankerCost),
      fmtAmt(waterIncome),
      fmtAmt(waterPending),
      fmtAmt(balance),
    ]);

    waterCarry = balance;
  }

  // Totals row
  waterRows.push([
    "Total",
    "—",
    fmtAmt(totalTankerCost),
    fmtAmt(totalWaterIncome),
    fmtAmt(totalWaterPending),
    fmtAmt(waterCarry),
  ]);

  autoTable(doc, {
    startY: y,
    head: [["Month", "Revolving Fund", "Tanker Cost", "Total Income", "Pending", "Balance"]],
    body: waterRows,
    ...TABLE_STYLES,
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right" },
      2: { halign: "right" },
      3: { halign: "right" },
      4: { halign: "right" },
      5: { halign: "right" },
    },
    didParseCell: (data) => {
      const isTotal = data.row.index === waterRows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 235, 255];
      }
      if (!isTotal && data.section === "body") {
        if (data.column.index === 4) data.cell.styles.textColor = [180, 0, 0]; // Pending in red
      }
    },
  });
  y = doc.lastAutoTable.finalY + 12;

  // ── SUMMARY BOX 1: CASH AVAILABLE ──
  // Check if we need a new page for the summary boxes
  if (y > doc.internal.pageSize.getHeight() - 70) {
    doc.addPage();
    y = 20;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 80);
  doc.text("Cash Available", 10, y);
  y += 3;

  const cashRows = [
    ["Maintenance Fund", fmtAmt(maintCarry)],
    ["Water Revolving Fund", fmtAmt(waterCarry)],
    ["Total Cash Available", fmtAmt(maintCarry + waterCarry)],
  ];

  autoTable(doc, {
    startY: y,
    head: [["Fund", "Balance"]],
    body: cashRows,
    ...TABLE_STYLES,
    tableWidth: (pageW - 20) / 2,
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right" },
    },
    didParseCell: (data) => {
      const isTotal = data.row.index === cashRows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 240, 220];
        data.cell.styles.textColor = [0, 100, 0];
      }
    },
  });
  const cashTableEndY = doc.lastAutoTable.finalY;

  // ── SUMMARY BOX 2: PENDING COLLECTIONS ──
  // Maintenance outstanding: sum of unpaid maintenance bills across all flats
  let maintOutstanding = 0;
  for (const flat of (flats || [])) {
    const monthlyRate = flat.maintenanceRate || settings?.maintenanceAmt || 1500;
    let carry = 0;
    for (const mk of months) {
      const payment = allPayments.find(p =>
        p.flatId === flat.flatId &&
        p.month === mk &&
        p.status === "confirmed" &&
        (!p.type || p.type.toLowerCase() === "maintenance" || p.type.toLowerCase() === "both")
      );
      const paid = payment ? Number(payment.amount || 0) : 0;
      carry = Math.max(carry + monthlyRate - paid, 0);
    }
    maintOutstanding += carry;
  }

  const pendingRows = [
    ["Maintenance Outstanding", fmtAmt(maintOutstanding)],
    ["Water Pending", fmtAmt(totalWaterPending)],
    ["Total Pending", fmtAmt(maintOutstanding + totalWaterPending)],
  ];

  // Place pending table on the right side, same Y as cash table
  autoTable(doc, {
    startY: cashTableEndY - ((cashRows.length + 1) * 8), // align top with cash table
    head: [["Fund", "Pending"]],
    body: pendingRows,
    ...TABLE_STYLES,
    tableWidth: (pageW - 20) / 2,
    margin: { left: pageW / 2 + 2, right: 8 },
    columnStyles: {
      0: { halign: "left" },
      1: { halign: "right" },
    },
    didParseCell: (data) => {
      const isTotal = data.row.index === pendingRows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [255, 235, 220];
        data.cell.styles.textColor = [160, 60, 0];
      }
      if (!isTotal && data.section === "body" && data.column.index === 1) {
        data.cell.styles.textColor = [180, 0, 0];
      }
    },
  });

  y = Math.max(cashTableEndY, doc.lastAutoTable.finalY) + 6;

  // ── FOOTER ──
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "This is a computer-generated statement. For queries contact the Management Committee.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 8,
    { align: "center" }
  );
  doc.text(
    "Generated via Adyantam Building Circle\u00ae",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 4,
    { align: "center" }
  );

  doc.save(`IncomeExpense_${fyStartYear}-${String(fyStartYear + 1).slice(-2)}.pdf`);
}

// ════════════════════════════════════════════════════════════════
// MAIN EXPORT 5 — generateWaterConsumptionReport
// Society-level: all flats, month-wise water units and bill amounts
// ════════════════════════════════════════════════════════════════
export async function generateWaterConsumptionReport({ settings, flats, allWaterBills, allMeterReadings }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();

  doc.internal.events.subscribe("addPage", () => drawLetterheadBackground(doc, settings));

  // Landscape letterhead — just draw logo at top portion
  if (settings?.letterheadImage) {
    try { doc.addImage(settings.letterheadImage, "JPEG", 0, 0, pageW, doc.internal.pageSize.getHeight(), undefined, "FAST"); } catch (e) {}
  }
  let y = 48; // slightly less margin in landscape

  const now = new Date();
  const fyStartYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  const months = getFYMonths();

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 80);
  doc.text(`Water Consumption Report — FY ${fyStartYear}-${String(fyStartYear + 1).slice(-2)}`, pageW / 2, y, { align: "center" });
  y += 6;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${now.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}`, pageW - 10, y, { align: "right" });
  y += 6;

  // Build columns: Flat | Owner | Apr Units | Apr Bill | May Units | May Bill | ... | Total Units | Total Bill
  const head = ["Flat", "Owner"];
  for (const mk of months) head.push(fmtMonth(mk) + "\nUnits", fmtMonth(mk) + "\nBill");
  head.push("Total\nUnits", "Total\nBill");

  const rows = [];
  const totalsUnits = new Array(months.length).fill(0);
  const totalsBill  = new Array(months.length).fill(0);
  let grandUnits = 0, grandBill = 0;

  for (const flat of flats) {
    const row = [flat.flatId, flat.ownerName || "—"];
    let flatUnits = 0, flatBill = 0;

    months.forEach((mk, i) => {
      const meter = allMeterReadings.find(r => r.flatId === flat.flatId && r.month === mk);
      const bill  = allWaterBills.find(w => w.flatId === flat.flatId && w.month === mk);
      const units = meter?.litersUsed != null ? meter.litersUsed : 0;
      const amt   = bill ? Number(bill.billAmount || bill.totalDue || 0) : 0;
      row.push(units > 0 ? String(units) : "—", amt > 0 ? fmtAmt(amt) : "—");
      flatUnits += units;
      flatBill  += amt;
      totalsUnits[i] += units;
      totalsBill[i]  += amt;
    });

    row.push(flatUnits > 0 ? String(flatUnits) : "—", flatBill > 0 ? fmtAmt(flatBill) : "—");
    grandUnits += flatUnits;
    grandBill  += flatBill;
    rows.push(row);
  }

  // Totals row
  const totalsRow = ["Total", ""];
  months.forEach((_, i) => {
    totalsRow.push(totalsUnits[i] > 0 ? String(totalsUnits[i]) : "—", totalsBill[i] > 0 ? fmtAmt(totalsBill[i]) : "—");
  });
  totalsRow.push(grandUnits > 0 ? String(grandUnits) : "—", grandBill > 0 ? fmtAmt(grandBill) : "—");
  rows.push(totalsRow);

  // Column styles — all right aligned except flat and owner
  const colStyles = { 0: { halign: "left" }, 1: { halign: "left" } };
  for (let i = 2; i < head.length; i++) colStyles[i] = { halign: "right" };

  autoTable(doc, {
    startY: y,
    head: [head],
    body: rows,
    styles: { fontSize: 7, textColor: [40, 40, 40] },
    headStyles: { fillColor: [30, 30, 60], textColor: [200, 200, 255], fontSize: 7, fontStyle: "bold", halign: "center" },
    columnStyles: colStyles,
    alternateRowStyles: { fillColor: [245, 245, 255] },
    margin: { left: 8, right: 8 },
    didParseCell: (data) => {
      const isTotal = data.row.index === rows.length - 1;
      if (isTotal) {
        data.cell.styles.fontStyle = "bold";
        data.cell.styles.fillColor = [220, 220, 240];
      }
    },
  });

  // Footer
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(140, 140, 140);
  doc.text(
    "This is a computer-generated statement. For queries contact the Management Committee.",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 6,
    { align: "center" }
  );
  doc.text(
    "Generated via Adyantam Building Circle\u00ae",
    pageW / 2,
    doc.internal.pageSize.getHeight() - 2,
    { align: "center" }
  );

  doc.save(`WaterConsumption_${fyStartYear}-${String(fyStartYear + 1).slice(-2)}.pdf`);
}
